'use client';

import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Link from '../components/SafeLink';

export default function SupportPage() {
  const [headcount, setHeadcount] = useState<number>(40);
  const [kdp, setKdp] = useState<boolean>(true);
  const [payroll, setPayroll] = useState<boolean>(false);
  const [helpdesk, setHelpdesk] = useState<boolean>(true);
  const [edo, setEdo] = useState<boolean>(true);
  const [onec, setOnec] = useState<boolean>(false);
  const [selectedPreset, setSelectedPreset] = useState<'basic' | 'standard' | 'pro'>('basic');

  const { perEmp, total } = useMemo(() => {
    let perEmp = 600;
    if (kdp) perEmp += 350;
    if (payroll) perEmp += 500;
    if (helpdesk) perEmp += 180;

    let monthly = headcount * perEmp;
    if (edo) monthly += 8000;
    if (onec) monthly += 12000;
    return { perEmp, total: monthly };
  }, [headcount, kdp, payroll, helpdesk, edo, onec]);

  const setPreset = (p: 'basic' | 'standard' | 'pro') => {
    setSelectedPreset(p);
    if (p === 'basic') {
      setKdp(true); setPayroll(false); setHelpdesk(true); setEdo(true); setOnec(false);
    } else if (p === 'standard') {
      setKdp(true); setPayroll(true); setHelpdesk(true); setEdo(true); setOnec(true);
    } else {
      setKdp(true); setPayroll(true); setHelpdesk(true); setEdo(true); setOnec(true);
    }
  };

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    consent: false,
  });

  const [formStatus, setFormStatus] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formData.consent) {
      setFormStatus('Пожалуйста, дайте согласие на обработку персональных данных.');
      return;
    }

    if (!formData.name || !formData.phone || !formData.email) {
      setFormStatus('Произошла ошибка: имя, телефон и email не могут быть пустыми.');
      return;
    }

    setFormStatus('Отправка...');

    const apiUrl = `${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/support-requests`;

    try {
      const { consent, ...dataToSend } = formData;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: dataToSend }),
      });

      if (response.ok) {
        setFormStatus('Ваша заявка успешно отправлена! Скоро мы с вами свяжемся.');
        setFormData({ name: '', phone: '', email: '', consent: false });
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.error?.message || 'Произошла неизвестная ошибка при отправке.';
        setFormStatus(`Произошла ошибка: ${errorMessage}.`);
        console.error('Ошибка:', errorData);
      }
    } catch (error) {
      setFormStatus('Ошибка сети. Пожалуйста, проверьте ваше подключение.');
      console.error('Ошибка:', error);
    }
  };

  const formatNumber = (num: number) => num.toLocaleString('ru-RU');

  return (
    <main className="min-h-screen bg-white text-[#0f172a] font-[Inter,system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif]">
      <section
        className="relative flex items-center min-h-[64vh] text-white overflow-hidden pt-[180px] pb-[120px]"
        style={{
          background: `radial-gradient(1200px 500px at 15% -10%, #2e5ec5 0%, rgba(46,94,197,.8) 30%, transparent 60%), radial-gradient(1500px 600px at 90% 10%, #0b1f56 0%, #071b42 60%, #05142c 100%)`,
        }}
      >
        <div className="w-full flex flex-col lg:flex-row items-start justify-between">
          <div className="lg:w-1/2 text-center lg:text-left mb-10 lg:mb-0 px-6 lg:pl-12">
            <span className="inline-flex items-center gap-2 bg-[rgba(255,255,255,.12)] border border-[rgba(255,255,255,.35)] text-white px-3 py-1 rounded-full text-xs mb-0">
              Направление: поддержка
            </span>
            <h1 className="text-[42px] font-extrabold mt-0 mb-2 leading-tight">Помощь в управлении персоналом</h1>
            <p className="text-lg text-white/80 max-w-base mx-auto lg:mx-0">
              Операционная HR‑поддержка, HR‑helpdesk и цифровые процессы: Кадровое делопроизводство (КДП), ЗП (1С:ЗУП), электронный документооборот (ЭДО), локальные нормативные акты (ЛНА), адаптация/увольнение и HR‑аналитика. соглашение об уровне обслуживания (SLA), отчётность и прозрачные пакеты.
            </p>
            <div className="mt-4 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <a href="#catalog" className="inline-flex items-center justify-center bg-[#FF7C22] text-white py-3 px-6 rounded-2xl font-bold hover:brightness-110 transition-all duration-200">
                Каталог услуг
              </a>
              <a href="#pricing" className="inline-flex items-center justify-center bg-white text-[#003B9B] border-2 border-[#003B9B] py-3 px-6 rounded-2xl font-bold hover:bg-gray-100 transition-all duration-200">
                Пакеты и калькулятор
              </a>
            </div>
          </div>
          <div className="lg:w-1/2 w-full flex flex-col lg:flex-row items-center justify-center lg:justify-end gap-4 mt-16 lg:mt-[80px] px-6">
            {[
              { value: '≤1 ч', label: 'первый ответ Helpdesk' },
              { value: '95%+', label: 'тикетов в срок SLA' },
              { value: '80+ NPS', label: 'удовлетворённость сотрудников' },
            ].map((card, idx) => (
              <div
                key={idx}
                className="bg-white text-[#003B9B] p-4 rounded-[16px] shadow-xl text-center w-full max-w-[280px] h-[80px] flex flex-col justify-center"
              >
                <div className="text-4xl font-extrabold">{card.value}</div>
                <div className="text-sm mt-1 text-gray-600">{card.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="catalog" className="py-16 bg-[#f6f8ff]">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-extrabold mb-10 text-[#0f172a]">Каталог услуг</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { h: 'Кадровое делопроизводство', p: 'Приём/перевод/увольнение, приказы, личные дела, трудовые, локальные нормативные акты(ЛНА), графики отпусков.' },
              { h: 'Зарплата (1С:ЗУП)', p: 'Расчёт ЗП, НДФЛ/взносы, ведомости, отчётность, банк‑клиент.' },
              { h: 'HR‑обслуживание', p: 'Заявки: справки, отпуска, командировки. Каналы: портал, email, мессенджеры.' },
              { h: 'Ввод/вывод сотрудников', p: 'Маршруты адаптации, приветственные наборы, чек‑листы, «чистый» выход.' },
              { h: 'ЛНА и регламенты', p: 'Правила внутреннего трудового распорядка (ПВТР), положения, политики, инструкции; актуализация по закону.' },
              { h: 'Обучение и развитие', p: 'Программы обучения, трекинг, база знаний.' },
              { h: 'HR‑аналитика', p: 'Дашборды: текучесть, явка, переработки, индекс лояльности и скорость закрытия заявок.' },
              { h: 'Интеграции', p: '1С:ЗУП, ЭДО (Диадок/СБИС), портал, HeadHunter.' },
              { h: 'Аудит и консалтинг', p: 'Аудит HR/ЗП, дорожная карта улучшений и автоматизации.' },
            ].map((b, i) => (
              <Card key={i} className="bg-white p-0 rounded-2xl shadow-sm border border-gray-200 text-left">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-[#0f172a]">{b.h}</h3>
                  <p className="text-base text-gray-700 mt-2">{b.p}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="py-16 bg-white">
        <div className="max-w-6xl mx-auto grid grid-cols-1 gap-10 px-6 lg:grid-cols-2">
          <Card className="rounded-[16px] p-0 shadow-[0_10px_28px_rgba(2,6,23,.08)] border border-[#e5e7eb]">
            <CardContent className="p-6">
              <h3 className="text-xl font-semibold">Пакеты обслуживания</h3>
              <div className="mt-3 flex gap-3">
                <button
                  onClick={() => setPreset('basic')}
                  className={selectedPreset === 'basic'
                    ? 'bg-[#FF7C22] text-white py-2 px-4 rounded-2xl font-bold hover:brightness-110 transition-all duration-200'
                    : 'bg-white text-[#003B9B] border-2 border-[#003B9B] py-2 px-4 rounded-2xl font-bold hover:bg-gray-100 transition-all duration-200'}
                >
                  Basic
                </button>
                <button
                  onClick={() => setPreset('standard')}
                  className={selectedPreset === 'standard'
                    ? 'bg-[#FF7C22] text-white py-2 px-4 rounded-2xl font-bold hover:brightness-110 transition-all duration-200'
                    : 'bg-white text-[#003B9B] border-2 border-[#003B9B] py-2 px-4 rounded-2xl font-bold hover:bg-gray-100 transition-all duration-200'}
                >
                  Standard
                </button>
                <button
                  onClick={() => setPreset('pro')}
                  className={selectedPreset === 'pro'
                    ? 'bg-[#FF7C22] text-white py-2 px-4 rounded-2xl font-bold hover:brightness-110 transition-all duration-200'
                    : 'bg-white text-[#003B9B] border-2 border-[#003B9B] py-2 px-4 rounded-2xl font-bold hover:bg-gray-100 transition-all duration-200'}
                >
                  Pro
                </button>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-4 items-center">
                <div>
                  <Label htmlFor="headcount" className="block text-sm text-gray-600 mb-1">Штат (чел.)</Label>
                  <Input
                    id="headcount"
                    type="number"
                    value={headcount}
                    onChange={(e) => setHeadcount(Number(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
                <div className="text-xs text-gray-500 mt-6">* ориентировочный расчёт</div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={kdp}
                    onChange={(e) => setKdp(e.target.checked)}
                    className="h-4 w-4 text-[#003B9B] border-gray-300 rounded focus:ring-[#003B9B]"
                  />
                  <span>КДП</span>
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={payroll}
                    onChange={(e) => setPayroll(e.target.checked)}
                    className="h-4 w-4 text-[#003B9B] border-gray-300 rounded focus:ring-[#003B9B]"
                  />
                  <span>Зарплата (1С:ЗУП)</span>
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={helpdesk}
                    onChange={(e) => setHelpdesk(e.target.checked)}
                    className="h-4 w-4 text-[#003B9B] border-gray-300 rounded focus:ring-[#003B9B]"
                  />
                  <span>HR‑Helpdesk</span>
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={edo}
                    onChange={(e) => setEdo(e.target.checked)}
                    className="h-4 w-4 text-[#003B9B] border-gray-300 rounded focus:ring-[#003B9B]"
                  />
                  <span>ЭДО</span>
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={onec}
                    onChange={(e) => setOnec(e.target.checked)}
                    className="h-4 w-4 text-[#003B9B] border-gray-300 rounded focus:ring-[#003B9B]"
                  />
                  <span>Интеграции 1С:ЗУП</span>
                </label>
              </div>

              <div className="card mt-6 p-4 border border-[#e5e7eb] shadow-none rounded-2xl">
                <div className="text-sm text-gray-600">Ставка</div>
                <div className="text-3xl font-extrabold text-[#003B9B]">{formatNumber(perEmp)} ₽ / сотрудник</div>
                <div className="text-sm text-gray-600 mt-2">Итог</div>
                <div className="text-4xl font-extrabold text-[#003B9B]">{formatNumber(total)} ₽ / мес</div>
              </div>
              <p className="text-xs text-gray-500 mt-4">
                Итоговые тарифы зависят от отрасли, графиков, интеграций и требований к отчётности.
              </p>
            </CardContent>
          </Card>

          <Card className="rounded-[16px] p-0 shadow-[0_10px_28px_rgba(2,6,23,.08)] border border-[#e5e7eb]">
            <CardContent className="p-6">
              <h3 className="text-xl font-semibold">Как мы работаем (30‑60‑90)</h3>
              <div className="overflow-x-auto">
                <table className="mt-3 w-full border-separate border-spacing-y-2 text-sm">
                  <thead>
                    <tr className="text-left text-gray-500">
                      <th className="rounded-l-[16px] bg-[#eef2ff] p-2 text-[#0f172a] font-semibold">Период</th>
                      <th className="bg-[#eef2ff] p-2 text-[#0f172a] font-semibold">Фокус</th>
                      <th className="rounded-r-[16px] bg-[#eef2ff] p-2 text-[#0f172a] font-semibold">Результат</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-white shadow-sm border border-[#e5e7eb] rounded-[16px]">
                      <td className="py-3 px-4 rounded-l-[16px] text-gray-700">0–30 дней</td>
                      <td className="py-3 px-4 text-gray-700">Аудит, перенос в ЭДО, запуск Helpdesk</td>
                      <td className="py-3 px-4 rounded-r-[16px] text-gray-700">Соглашения об уровне сервиса, каталог услуг, чек‑листы</td>
                    </tr>
                    <tr className="bg-white shadow-sm border border-[#e5e7eb] rounded-[16px]">
                      <td className="py-3 px-4 rounded-l-[16px] text-gray-700">31–60 дней</td>
                      <td className="py-3 px-4 text-gray-700">КДП и ЗП по регламенту, обучение менеджеров</td>
                      <td className="py-3 px-4 rounded-r-[16px] text-gray-700">Стабильные расчёты ЗП, прозрачные статусы</td>
                    </tr>
                    <tr className="bg-white shadow-sm border border-[#e5e7eb] rounded-[16px]">
                      <td className="py-3 px-4 rounded-l-[16px] text-gray-700">61–90 дней</td>
                      <td className="py-3 px-4 text-gray-700">HR‑аналитика, автоматизация, улучшения</td>
                      <td className="py-3 px-4 rounded-r-[16px] text-gray-700">Дашборды, индекс лояльности сотрудников, отчёт по эффекту</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="mt-6 flex flex-wrap gap-2">
                <span className="bg-[#eef2ff] text-[#1e293b] text-xs px-3 py-1 rounded-full border border-[#e5e7eb]">1С:ЗУП</span>
                <span className="bg-[#eef2ff] text-[#1e293b] text-xs px-3 py-1 rounded-full border border-[#e5e7eb]">Диадок/СБИС</span>
                <span className="bg-[#eef2ff] text-[#1e293b] text-xs px-3 py-1 rounded-full border border-[#e5e7eb]">Портал</span>
                <span className="bg-[#eef2ff] text-[#1e293b] text-xs px-3 py-1 rounded-full border border-[#e5e7eb]">HeadHunter API</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="py-[64px]">
        <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-[22px]">
          <Card className="rounded-[16px] p-[20px] shadow-[0_10px_28px_rgba(2,6,23,.08)] border border-[#e5e7eb]">
            <h3 className="text-xl font-semibold mb-3">Кейс: складская логистика</h3>
            <p className="text-[#64748b] text-base">
              +16% к выработке, −27% переработок, −18% текучесть за 90 дней. Экономия ФОТ 5,2 млн ₽/кв.
            </p>
          </Card>
          <Card className="rounded-[16px] p-[20px] shadow-[0_10px_28px_rgba(2,6,23,.08)] border border-[#e5e7eb]">
            <h3 className="text-xl font-semibold">Готовы обсудить?</h3>
            <p className="text-[#64748b] text-base">Оставьте контакты — подготовим предложение и чек‑лист подключения.</p>
            <form onSubmit={handleSubmit}>
              <div className="flex flex-col sm:flex-row gap-[20px] mt-[8px] flex-wrap">
                <div className="flex-1">
                  <Label htmlFor="contactName" className="block text-[12px] text-[#64748b] mb-[6px]">Имя</Label>
                  <Input
                    id="contactName"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Как к вам обращаться"
                    className="w-full px-[12px] py-[10px] border border-[#d1d5db] rounded-[10px]"
                  />
                </div>
                <div className="flex-1">
                  <Label htmlFor="contactPhone" className="block text-[12px] text-[#64748b] mb-[6px]">Телефон</Label>
                  <Input
                    id="contactPhone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+7 (___) ___‑__‑__"
                    className="w-full px-[12px] py-[10px] border border-[#d1d5db] rounded-[10px]"
                  />
                </div>
                <div className="flex-1">
                  <Label htmlFor="contactEmail" className="block text-[12px] text-[#64748b] mb-[6px]">E-mail</Label>
                  <Input
                    id="contactEmail"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="work@company.com"
                    className="w-full px-[12px] py-[10px] border border-[#d1d5db] rounded-[10px]"
                  />
                </div>
              </div>

              <div className="mt-4 flex items-center">
                <input
                  id="consent"
                  name="consent"
                  type="checkbox"
                  checked={formData.consent}
                  onChange={handleInputChange}
                  className="
                    h-5 w-5 rounded border-2 border-gray-400 cursor-pointer 
                    checked:bg-[#FF7C22] checked:border-[#FF7C22]
                    focus:ring-2 focus:ring-[#003B9B] 
                    appearance-none shrink-0 transition-all duration-200
                  "
                  style={{
                    backgroundImage: formData.consent
                      ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23ffffff' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'%3E%3C/polyline%3E%3C/svg%3E")`
                      : "none",
                    backgroundSize: "80%",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                  }}
                />
                <label
                  htmlFor="consent"
                  className="ml-3 text-sm text-gray-700 cursor-pointer leading-tight"
                >
                  Я соглашаюсь с{' '}
                  <Link href="/privacy" className="text-[#FF7C22] hover:underline">
                    обработкой персональных данных
                  </Link>
                </label>
              </div>

              <div className="mt-6">
                <button
                  type="submit"
                  className="bg-[#FF7C22] hover:bg-orange-700 text-white rounded-2xl font-bold py-3 px-5 transition-all duration-200 ease-in-out transform hover:-translate-y-0.5"
                >
                  Отправить заявку
                </button>
              </div>
            </form>
            {formStatus && (
              <p className={`mt-2 text-sm ${formStatus.startsWith('Произошла ошибка') || formStatus.includes('Ошибка сети')
                ? 'text-red-500'
                : formStatus.includes('Пожалуйста, дайте согласие')
                  ? 'text-amber-600'
                  : 'text-green-600'
                }`}>
                {formStatus}
              </p>
            )}
          </Card>
        </div>
      </section>
    </main>
  );
}